import { DialogContent, DialogContentText, Typography } from '@material-ui/core'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import LinearProgress from '@material-ui/core/LinearProgress'
import { JSXElementConstructor, ReactElement, useState } from 'react'
import { Toolbar, usePermissions } from 'react-admin'
import { ConfirmDialog } from 'shared/components/Dialog/ConfirmDialog'
import { Handler } from 'shared/helpers/typeHelper'

import { useHasAllowLoadRecoveryAutomatedTransitions } from 'hooks/useHasFeatureFlag'
import { FSM_TRANSITION_RECORD_TYPE } from '../../../helpers/constants'
import { getDriverTransitionProps } from '../Transitions/DriverFSMTransitions'
import { getInvoiceTransitionProps } from '../Transitions/InvoiceFSMTransitions'
import { getIssueTransitionProps } from '../Transitions/IssueFSMTransitions'
import { getJobTransitionProps } from '../Transitions/JobFSMTransitions'
import { getTeamTransitionProps } from '../Transitions/TeamFSMTransitions'
import { DriverTransitionButton } from './DriverTransitionButton'
import { InvoiceTransitionButton } from './InvoiceTransitionButton'
import { IssueTransitionButton } from './IssueTransitionButton'
import { JobTransitionButton } from './JobTransitionButton'
import { TeamTransitionButton } from './TeamTransitionButton'

export type XTransitionButtonProps = {
  record: any
  transitionProps: any
  classes?: any
  loading: boolean
  setLoading: (loading: boolean) => void
  children?: ReactElement<any, string | JSXElementConstructor<any>>
  onSuccess?: Handler
}

type FSMTransitionButtonProps = {
  record: any
  transitionProps: any
  className?: string
  handleSubmit: () => any
  transitionAllowed?: boolean
  loading?: boolean
  children?: React.ReactNode
  button?: ReactElement<any, string | JSXElementConstructor<any>>
  finalConfirmation?: boolean
  finalConfirmationDialogContent?: React.ReactNode
}

/** This is the basic modal component to prompt a user to confirm a transition.
 * This should be used as the base for different FSM_TRANSITION_RECORD_TYPE.
 * You can pass this component children, if a transition modal needs to take in form values.
 */
export const FSMTransitionButton = ({
  record,
  transitionProps,
  className = '',
  children,
  button,
  handleSubmit,
  transitionAllowed = true,
  loading = false,
  finalConfirmation = false,
  finalConfirmationDialogContent,
}: FSMTransitionButtonProps) => {
  const {
    /* vars */
    confirmationMessage,
    transitionString,
    transitionTitle,
  } = transitionProps

  return (
    <div>
      <ConfirmDialog
        handleConfirm={handleSubmit}
        dialogContent={
          <>
            <Typography variant="h4" align="center">
              {transitionTitle ? transitionTitle(record) : transitionString}
            </Typography>
            <DialogContent>
              <DialogContentText>{confirmationMessage(record)}</DialogContentText>
              {children}
            </DialogContent>
          </>
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        finalConfirmation={finalConfirmation}
        finalConfirmationDialogContent={finalConfirmationDialogContent}
      >
        {button ? (
          button
        ) : (
          <Button
            disabled={loading}
            className={className}
            variant="contained"
            color={transitionAllowed ? 'primary' : 'secondary'}
          >
            {transitionString}
          </Button>
        )}
      </ConfirmDialog>
    </div>
  )
}

type FSMButtonToolbarProps = {
  record: any
  recordType: string
  classes: any
  isHidden: boolean
  includeCancel?: boolean
  isLoading?: boolean
  children?: React.ReactNode
}

export const FSMButtonToolbar = ({
  record,
  recordType,
  classes,
  isHidden,
  isLoading: isLoadingProp,
  includeCancel = true,
  children = null,
}: FSMButtonToolbarProps) => {
  const [isRequestInProgress, setRequestInProgress] = useState(false)
  const { permissions } = usePermissions()
  const allowLoadRecoveryAutomatedTransitions = useHasAllowLoadRecoveryAutomatedTransitions()

  if (!record || !recordType || isHidden) {
    return null
  }

  const isLoading = isRequestInProgress || !!isLoadingProp

  let transitions
  let TransitionComponent: any
  if (recordType === FSM_TRANSITION_RECORD_TYPE.job) {
    transitions = getJobTransitionProps(record, includeCancel, allowLoadRecoveryAutomatedTransitions)
    TransitionComponent = JobTransitionButton
  } else if (recordType === FSM_TRANSITION_RECORD_TYPE.invoice) {
    transitions = getInvoiceTransitionProps(record)
    TransitionComponent = InvoiceTransitionButton
  } else if (recordType === FSM_TRANSITION_RECORD_TYPE.issue) {
    transitions = getIssueTransitionProps(record)
    TransitionComponent = IssueTransitionButton
  } else if (recordType === FSM_TRANSITION_RECORD_TYPE.driver) {
    transitions = getDriverTransitionProps(record)
    TransitionComponent = DriverTransitionButton
  } else if (recordType === FSM_TRANSITION_RECORD_TYPE.team) {
    transitions = getTeamTransitionProps(record, permissions)
    TransitionComponent = TeamTransitionButton
  } else {
    console.warn('FSMButtonGrid: recordType not recognized')
    return null
  }

  if (!transitions) return null

  return (
    <Toolbar>
      <Grid container direction="column" spacing={2}>
        <Grid item xs>
          <Grid container spacing={4}>
            {transitions.map((transitionProps: any, i: any) => (
              <Grid key={i} item>
                <TransitionComponent
                  record={record}
                  recordType={recordType}
                  transitionProps={transitionProps}
                  classes={classes}
                  loading={isLoading}
                  setLoading={setRequestInProgress}
                />
              </Grid>
            ))}
            {children}
          </Grid>
        </Grid>
        {isLoading && (
          <Grid item xs>
            <Box marginBottom={2}>
              <LinearProgress />
            </Box>
          </Grid>
        )}
      </Grid>
    </Toolbar>
  )
}
